import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Select from "@mui/material/Select"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useRef, useState } from "react"

import FullCalendar from "@fullcalendar/react"

import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import timeGridPlugin from "@fullcalendar/timegrid"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import AdminLiquidToolbar from "../components/AdminLiquidToolbar"
import AdminPageHeader from "../components/AdminPageHeader"
import MeetingDetailsDialog from "./MeetingDetailsDialog"
import MeetingStatusChip from "./MeetingStatusChip"
import MeetingStatusLegend from "./MeetingStatusLegend"
import MeetingsBookButton from "./MeetingsBookButton"
import { renderEventContent, toCalendarEvent } from "./meetingCalendarEvents"
import {
  MEETING_STATUS_FILTER_OPTIONS,
  formatMeetingDateTime,
  meetingCandidateNames,
  statusMeta,
} from "./meetingUtils"
import "./meetingsCalendar.css"

export default function MeetingsCalendarPage() {
  const calendarRef = useRef(null)
  const [statusFilter, setStatusFilter] = useState("")
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedMeetingId, setSelectedMeetingId] = useState(null)
  const [upcoming, setUpcoming] = useState([])
  const [isUpcomingLoading, setIsUpcomingLoading] = useState(true)
  const [calendarError, setCalendarError] = useState(false)

  const loadEvents = useCallback(
    (info, success, failure) => {
      adminApi
        .fetchMeetingsCalendar({
          start: info.startStr,
          end: info.endStr,
          status: statusFilter || undefined,
        })
        .then(({ data }) => {
          setCalendarError(false)
          success(data.map(toCalendarEvent))
        })
        .catch((error) => {
          setCalendarError(true)
          failure(error)
        })
    },
    [statusFilter]
  )

  useEffect(() => {
    calendarRef.current?.getApi().refetchEvents()
  }, [statusFilter, refreshKey])

  useEffect(() => {
    let cancelled = false
    setIsUpcomingLoading(true)
    adminApi
      .fetchMeetings({
        status: "upcoming",
        ordering: "start_datetime",
        page_size: 8,
      })
      .then(({ data }) => {
        if (!cancelled) setUpcoming(data.results || [])
      })
      .catch(() => {
        if (!cancelled) setUpcoming([])
      })
      .finally(() => {
        if (!cancelled) setIsUpcomingLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  function refresh() {
    setRefreshKey((key) => key + 1)
  }

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Meetings"
        subtitle="Every meeting scheduled on a user's Google or Outlook calendar."
        actions={<MeetingsBookButton onBooked={refresh} />}
      />
      <AdminLiquidToolbar>
        <Select
          size="small"
          displayEmpty
          inputProps={{ "aria-label": "Status" }}
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {MEETING_STATUS_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value || "all"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <MeetingStatusLegend
          selected={statusFilter}
          onSelect={setStatusFilter}
        />
        {calendarError ? (
          <Typography variant="body2" color="error">
            Could not load meetings for this range.
          </Typography>
        ) : null}
      </AdminLiquidToolbar>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={9}>
          <Paper variant="outlined" sx={{ p: 2 }} className="meetings-calendar">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              height="auto"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              buttonText={{
                today: "Today",
                month: "Month",
                week: "Week",
                day: "Day",
              }}
              events={loadEvents}
              eventDisplay="block"
              eventContent={renderEventContent}
              eventClick={(info) =>
                setSelectedMeetingId(info.event.extendedProps.meetingId)
              }
              dayMaxEvents={4}
              moreLinkText={(count) => `+${count} more`}
              nowIndicator
            />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Upcoming meetings
            </Typography>
            {isUpcomingLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : null}
            {!isUpcomingLoading && upcoming.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No upcoming meetings.
              </Typography>
            ) : null}
            <Column gap={1}>
              {upcoming.map((meeting) => {
                const { calendarColor, chip } = statusMeta(
                  meeting.display_status
                )
                return (
                  <Box
                    key={meeting.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedMeetingId(meeting.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter")
                        setSelectedMeetingId(meeting.id)
                    }}
                    sx={{
                      cursor: "pointer",
                      p: 1,
                      borderRadius: 1,
                      borderLeft: `4px solid ${calendarColor}`,
                      backgroundColor: "action.hover",
                      "&:hover, &:focus-visible": {
                        backgroundColor: chip.background,
                        outline: "none",
                      },
                    }}
                  >
                    <Row
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatMeetingDateTime(meeting.start_datetime)}
                      </Typography>
                      <MeetingStatusChip status={meeting.display_status} />
                    </Row>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      title={meetingCandidateNames(meeting)}
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {meetingCandidateNames(meeting)}
                    </Typography>
                    {meeting.host ? (
                      <Typography variant="caption" color="text.secondary">
                        {meeting.host.name}
                      </Typography>
                    ) : null}
                  </Box>
                )
              })}
            </Column>
          </Paper>
        </Grid>
      </Grid>
      <MeetingDetailsDialog
        open={selectedMeetingId !== null}
        meetingId={selectedMeetingId}
        onClose={() => setSelectedMeetingId(null)}
        onChanged={refresh}
      />
    </Column>
  )
}
